import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Admission,
  Connection,
  Floor,
  HospitalInformationSection,
  InformationGroup,
} from '@quality24/inpatient-typings';
import { Spinner } from '@quality24/design-system';
import classNames from 'classnames';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router';

import {
  getFloorId,
  getRoomId,
} from '@/inpatient-patient-pwa/services/storage';
import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';

import HospitalInformationContent from './HospitalInformationContent';

/**
 * Placeholder id passed for `informationGroupById` when there is no `group_id`.
 * The group selection is gated by `@include(if: $includeGroup)`, so this value
 * is never actually read — it only satisfies the non-null `id` argument.
 */
export const EMPTY_UUID = '00000000-0000-4000-0000-000000000000';

/**
 * `filter` is built by the caller so one query serves both the unscoped and
 * group-scoped listings; the scoped group is fetched only when `$includeGroup`.
 */
export const SELECT_QUERY = gql`
  query FetchHospitalInformation(
    $admissionId: UUID!
    $floorId: UUID!
    $filter: HospitalInformationSectionFilter!
    $groupId: UUID!
    $includeGroup: Boolean!
  ) {
    admissionById(id: $admissionId) {
      id
      wifiLogin
      wifiPassword
      hospital {
        hospitalInformationSections(orderBy: ORDER_ASC, filter: $filter) {
          nodes {
            id
            data
            titleI18N
            buttonLabelI18N
            contentI18N
            type
            trackable
            children: childHospitalInformationSections(
              orderBy: ORDER_ASC
              filter: { enabled: { equalTo: true } }
            ) {
              nodes {
                id
                data
                titleI18N
                buttonLabelI18N
                contentI18N
                type
                trackable
              }
            }
          }
        }
      }
    }

    informationGroup: informationGroupById(id: $groupId)
      @include(if: $includeGroup) {
      id
      nameI18N
      descriptionI18N
    }

    floorById(id: $floorId) {
      id
      unitId
    }
  }
`;

type HospitalInformationFields =
  | 'id'
  | 'data'
  | 'titleI18N'
  | 'buttonLabelI18N'
  | 'contentI18N'
  | 'type'
  | 'trackable';

type ParsedHospitalInformation = Pick<
  HospitalInformationSection,
  HospitalInformationFields
> & {
  children?: Pick<HospitalInformationSection, HospitalInformationFields>[];
};

type HospitalInformation = Pick<
  HospitalInformationSection,
  HospitalInformationFields
> & {
  children?: Connection<
    Pick<HospitalInformationSection, HospitalInformationFields>
  >;
};

export interface QueryResult {
  admissionById: Pick<Admission, 'id' | 'wifiLogin' | 'wifiPassword'> & {
    hospital: {
      hospitalInformationSections: Connection<HospitalInformation>;
    };
  };
  informationGroup?: Pick<
    InformationGroup,
    'id' | 'nameI18N' | 'descriptionI18N'
  >;
  floorById: Pick<Floor, 'id' | 'unitId'>;
}

export interface Props {
  className?: string;
}

/**
 * Checks if we should display this information
 * @param info
 * @param param1
 * @returns
 */
const checkCondition = (
  info: HospitalInformation,
  {
    unitId,
    floorId,
    roomId,
  }: {
    unitId: string | null;
    floorId: string;
    roomId: string;
  },
) => {
  if (!info.data.condition) return true;
  if (
    !info.data.condition.unitId &&
    !info.data.condition.floorId &&
    !info.data.condition.roomId
  ) {
    return true;
  }

  let result = true;
  if (info.data.condition.unitId) {
    const { includes, excludes } = info.data.condition.unitId;
    if (includes && Array.isArray(includes)) {
      result = result && !!includes.find((id) => id === unitId);
    }
    if (excludes && Array.isArray(excludes)) {
      result = result && !excludes.find((id) => id === unitId);
    }
  }
  if (info.data.condition.floorId) {
    const { includes, excludes } = info.data.condition.floorId;
    if (includes && Array.isArray(includes)) {
      result = result && !!includes.find((id) => id === floorId);
    }
    if (excludes && Array.isArray(excludes)) {
      result = result && !excludes.find((id) => id === floorId);
    }
  }
  if (info.data.condition.roomId) {
    const { includes, excludes } = info.data.condition.roomId;
    if (includes && Array.isArray(includes)) {
      result = result && !!includes.find((id) => id === roomId);
    }
    if (excludes && Array.isArray(excludes)) {
      result = result && !excludes.find((id) => id === roomId);
    }
  }
  return result;
};

const parseHospitalInformation = (
  nodes: HospitalInformation[] | undefined,
): ParsedHospitalInformation[] => {
  if (!nodes) return [];

  return nodes.map((node) => ({
    id: node.id,
    data: node.data,
    titleI18N: node.titleI18N,
    buttonLabelI18N: node.buttonLabelI18N,
    contentI18N: node.contentI18N,
    type: node.type,
    trackable: node.trackable,
    children:
      node.children?.nodes && node.children?.nodes.length > 0
        ? parseHospitalInformation(node.children?.nodes)
        : undefined,
  }));
};

/**
 * HospitalInformationPage component
 */
const HospitalInformationPage: React.FunctionComponent<Props> = ({
  className,
}) => {
  const { t } = useTranslation();
  const {
    user: { admissionId },
  } = useAuth();
  const floorId = getFloorId();
  const roomId = getRoomId();

  // When opened from an APP_FRONT_PAGE `informationGroup` button we scope the
  // sections to that hospital_information_group.
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group_id');

  const filter = React.useMemo(
    () => ({
      hideOnPwa: { equalTo: false },
      parentId: { isNull: true },
      enabled: { equalTo: true },
      ...(groupId ? { groupId: { equalTo: groupId } } : {}),
    }),
    [groupId],
  );

  // The scoped group is only fetched when a group_id is present; otherwise the
  // group selection is skipped via @include and `groupId` gets a placeholder.
  const { data, loading, error } = useQuery<QueryResult>(SELECT_QUERY, {
    variables: {
      admissionId,
      floorId,
      filter,
      groupId: groupId ?? EMPTY_UUID,
      includeGroup: !!groupId,
    },
    skip: !admissionId,
  });

  const information: ParsedHospitalInformation[] = React.useMemo(
    () =>
      parseHospitalInformation(
        data?.admissionById?.hospital?.hospitalInformationSections?.nodes.filter(
          (info) =>
            checkCondition(info, {
              unitId: data.floorById?.unitId || null,
              floorId,
              roomId,
            }),
        ),
      ),
    [
      data?.admissionById?.hospital?.hospitalInformationSections?.nodes,
      data?.floorById?.unitId,
      floorId,
      roomId,
    ],
  );

  return (
    <>
      <Helmet>
        <title>{t('hospitalInfo.pageTitle')}</title>
      </Helmet>

      <div className={classNames(className, 'd-flex flex-column px-4')}>
        {!loading && !error && data ? (
          <HospitalInformationContent
            information={information}
            informationGroup={data?.informationGroup}
            wifiLogin={data?.admissionById.wifiLogin}
            wifiPassword={data?.admissionById.wifiPassword}
          />
        ) : (
          <div className="d-flex flex-justify-center p-4">
            <Spinner />
          </div>
        )}
      </div>
    </>
  );
};

export default HospitalInformationPage;
